/* eslint-disable camelcase, padded-blocks */
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockHeading } from 'components/BlockHeading';
import { getConfiguratorData } from 'api/data';
import { Spinner } from 'components/Spinner';
import { GlassStructureContext } from 'context/configuration';
import GlassDB from 'components/ThreeJs/assets/filter/GlassDB';
import FoldableStatusList from './partials/FoldableStatusList';

const AdminDashboardSiteInfo = () => {
	const { t } = useTranslation();
	const [status, setStatus] = useState(null);
	const { uiHandlerRef } = useContext(GlassStructureContext);

	const isNotEmptyString = (str, field) => {
		if (!str.trim()) {
			return {
				ok: false,
				err: t('adminSiteInfo:fieldEmptyOrWhitespaceFmt').replace('{field}', field),
			};
		}
		return {
			ok: true,
			value: t('adminSiteInfo:fieldValueFmt').replace('{field}', field).replace('{value}', str),
		};
	};

	const isValidNum = (num, field, { min, max }) => {
		const n = parseFloat(num);
		if (isNaN(n)) {
			return {
				ok: false,
				err: t('adminSiteInfo:fieldNotANumberFmt').replace('{field}', field).replace('{num}', num),
			};
		} else if (min !== undefined && n < min) {
			return {
				ok: false,
				err: t('adminSiteInfo:fieldLessThanMinFmt')
					.replace('{field}', field)
					.replace('{num}', num)
					.replace('{min}', min),
			};
		} else if (max !== undefined && n > max) {
			return {
				ok: false,
				err: t('adminSiteInfo:fieldGreaterThanMaxFmt')
					.replace('{field}', field)
					.replace('{num}', num)
					.replace('{max}', max),
			};
		}
		return {
			ok: true,
			value: t('adminSiteInfo:fieldNumValueFmt').replace('{field}', field).replace('{n}', n),
		};
	};

	const isValidCsv = (csv_str, field, { min_cols, max_cols, min_rows }) => {
		if (!csv_str)
			return { ok: false, err: t('adminSiteInfo:fieldEmptyFmt').replace('{field}', field) };
		const lines = csv_str.split('\n');
		if (min_rows !== undefined && lines.length < min_rows) {
			return {
				ok: false,
				err: t('adminSiteInfo:fieldOnlyRowsLongFmt')
					.replace('{field}', field)
					.replace('{lines}', lines.length)
					.replace('{min_rows}', min_rows),
			};
		}
		if (min_cols !== undefined || max_cols !== undefined) {
			const errs = [];
			if (min_cols !== undefined || max_cols !== undefined) {
				lines.forEach((l, i) => {
					if (errs.length > 2) return;
					const cols = l.split(',');
					if (min_cols !== undefined && cols.length < min_cols) {
						errs.push(
							t('adminSiteInfo:rowOnlyEntriesLongFmt')
								.replace('{row}', i + 1)
								.replace('{cols}', cols.length)
								.replace('{min_cols}', min_cols)
						);
					}
					if (max_cols !== undefined && cols.length > max_cols) {
						errs.push(
							t('adminSiteInfo:rowTooManyEntriesFmt')
								.replace('{row}', i + 1)
								.replace('{cols}', cols.length)
								.replace('{max_cols}', max_cols)
						);
					}
				});
			}
			if (errs.length) {
				return {
					ok: false,
					err: t('adminSiteInfo:fieldErrorsFmt')
						.replace('{field}', field)
						.replace('{errors}', errs.join(', ')),
				};
			}
		}
		return {
			ok: true,
			value: t('adminSiteInfo:fieldHasRowsFmt')
				.replace('{field}', field)
				.replace('{lines}', lines.length),
		};
	};

	const isValidJson = (json_str, field, { min, max, type } = {}) => {
		try {
			const data = JSON.parse(json_str);
			const dt = typeof data;
			if (!data) {
				return { ok: false, err: t('adminSiteInfo:fieldEmptyFmt').replace('{field}', field) };
			} else if (Array.isArray(data)) {
				if (type && type !== 'array') {
					return { ok: false, err: t('adminSiteInfo:fieldNotAListFmt').replace('{field}', field) };
				}
				if (min !== undefined && data.length < min) {
					return {
						ok: false,
						err: t('adminSiteInfo:fieldOnlyEntriesFmt')
							.replace('{field}', field)
							.replace('{length}', data.length)
							.replace('{min}', min),
					};
				}
				if (max !== undefined && data.length > max) {
					return {
						ok: false,
						err: t('adminSiteInfo:fieldTooManyEntriesFmt')
							.replace('{field}', field)
							.replace('{length}', data.length)
							.replace('{max}', max),
					};
				}
				return {
					ok: true,
					value: t('adminSiteInfo:fieldHasEntriesFmt')
						.replace('{field}', field)
						.replace('{length}', data.length),
				};
			} else if (dt === 'object') {
				if (type && type !== 'object') {
					return {
						ok: false,
						err: t('adminSiteInfo:fieldNotAnObjectFmt').replace('{field}', field),
					};
				}
				const len = Object.keys(data).length;
				if (min !== undefined && len < min) {
					return {
						ok: false,
						err: t('adminSiteInfo:fieldOnlyEntriesFmt')
							.replace('{field}', field)
							.replace('{length}', len)
							.replace('{min}', min),
					};
				}
				if (max !== undefined && len > max) {
					return {
						ok: false,
						err: t('adminSiteInfo:fieldTooManyEntriesFmt')
							.replace('{field}', field)
							.replace('{length}', len)
							.replace('{max}', max),
					};
				}
				return {
					ok: true,
					value: t('adminSiteInfo:fieldHasEntriesFmt')
						.replace('{field}', field)
						.replace('{length}', len),
				};
			} else {
				if (type && dt !== type)
					return {
						ok: false,
						err: t('adminSiteInfo:fieldNotOfTypeFmt')
							.replace('{field}', field)
							.replace('{type}', type),
					};
				return {
					ok: true,
					value: t('adminSiteInfo:fieldDataValueFmt')
						.replace('{field}', field)
						.replace('{data}', data),
				};
			}
		} catch (e) {
			return {
				ok: false,
				err: t('adminSiteInfo:jsonFormatErrorFmt')
					.replace('{field}', field)
					.replace('{message}', e.message),
			};
		}
	};

	const isValidGlassDb = (db, glassLabel, { min }) => {
		if (!db?.loaded) return { ok: false, err: t('adminSiteInfo:glassDbNotLoaded') };
		let errs = [];
		db.db[glassLabel].forEach(g => {
			const e = g.checkErrors();
			if (e.length > 0) {
				errs = [...errs, ...e];
			}
		});
		const len = db.db[glassLabel].length;
		const numLoadedMsg = t('adminSiteInfo:glassLoadedFmt')
			.replace('{glassLabel}', glassLabel)
			.replace('{len}', len);
		if (min && len < min) {
			errs.push(
				t('adminSiteInfo:fieldOnlyEntriesFmt')
					.replace('{field}', glassLabel)
					.replace('{length}', len)
					.replace('{min}', min)
			);
		}
		if (errs.length > 0) {
			errs.push(numLoadedMsg);
			return { ok: false, err: errs.join('; ') };
		}

		return { ok: true, value: numLoadedMsg };
	};

	const isValidUValueDb = (db, struct, { min }) => {
		if (!db?.loaded) return { ok: false, err: t('adminSiteInfo:glassDbNotLoaded') };

		const len = db.db.uValue[struct].length;
		if (min && len < min) {
			return {
				ok: false,
				err: t('adminSiteInfo:uValueDbOnlyEntriesFmt')
					.replace('{struct}', struct)
					.replace('{len}', len)
					.replace('{min}', min),
			};
		}

		return {
			ok: true,
			value: t('adminSiteInfo:uValueDbHasEntriesFmt')
				.replace('{struct}', struct)
				.replace('{len}', len),
		};
	};

	const isValidGlassPrices = (db, { minEntriesPerGlazing = 5 }) => {
		if (!db?.loaded) return { ok: false, err: t('adminSiteInfo:glassDbNotLoaded') };
		if (!db.db.glassPrices)
			return { ok: false, err: t('adminSiteInfo:fieldEmptyFmt').replace('{field}', 'glassPrices') };

		const { glassPrices } = db.db;
		const requiredGlazingTypes = ['1-glazed', '2-glazed', '3-glazed'];

		// Check that category "0" exists
		if (!glassPrices['0']) {
			return {
				ok: false,
				err: t('adminSiteInfo:glassPricesMissingCategoryFmt').replace('{category}', '0'),
			};
		}

		const categories = Object.keys(glassPrices);
		let totalPrices = 0;

		// Validate each category
		let validationError = null;
		categories.forEach(category => {
			if (validationError) return;
			const categoryData = glassPrices[category];

			// Check each required glazing type exists
			requiredGlazingTypes.forEach(glazing => {
				if (validationError) return;
				if (!categoryData[glazing]) {
					validationError = {
						ok: false,
						err: t('adminSiteInfo:glassPricesMissingGlazingFmt')
							.replace('{category}', category)
							.replace('{glazing}', glazing),
					};
					return;
				}

				const glazingData = categoryData[glazing];
				const entries = Object.keys(glazingData).length;

				// Check minimum entries
				if (entries < minEntriesPerGlazing) {
					validationError = {
						ok: false,
						err: t('adminSiteInfo:glassPricesTooFewEntriesFmt')
							.replace('{category}', category)
							.replace('{glazing}', glazing)
							.replace('{count}', entries)
							.replace('{min}', minEntriesPerGlazing),
					};
					return;
				}

				// Count non-NaN prices
				if (glazing === '1-glazed') {
					// For 1-glazed, values are direct numbers
					Object.values(glazingData).forEach(price => {
						if (!isNaN(price)) totalPrices += 1;
					});
				} else {
					// For 2-glazed and 3-glazed, values are arrays
					Object.values(glazingData).forEach(priceArray => {
						if (Array.isArray(priceArray)) {
							priceArray.forEach(price => {
								if (!isNaN(price.price)) totalPrices += 1;
							});
						}
					});
				}
			});
		});

		if (validationError) return validationError;

		return {
			ok: true,
			value: t('adminSiteInfo:glassPricesLoadedFmt')
				.replace('{categories}', categories.length)
				.replace('{totalPrices}', totalPrices),
		};
	};

	useEffect(() => {
		let mounted = true;
		(async () => {
			const res = { allGood: true };
			const cd = await getConfiguratorData();
			const {
				datasheets,
				email,
				glassdb,
				pricing,
				extra_text_from_volume,
				sound_dampening,
				generic_frgs,
			} = cd;

			// cd.pricing
			if (cd.pricing) {
				res.pricing = { ok: true };
			} else {
				res.pricing = { ok: false, err: t('adminSiteInfo:pricingDataMissing') };
			}
			res.min_pos_sqm = isValidNum(pricing.min_pos_sqm, t('adminSiteInfo:minSquareMeters'), {
				min: 0,
			});
			res.model_glass_factor = isValidNum(
				pricing.model_glass_factor,
				t('adminSiteInfo:modelGlassFactor'),
				{
					min: 0,
					max: 4,
				}
			);
			res.vat_percent = isValidNum(pricing.vat_percent, t('adminSiteInfo:vatRate'), {
				min: 0,
				max: 50,
			});
			res.lsva_per_m2 = isValidNum(pricing.lsva_per_m2, t('adminSiteInfo:lsvaPerM2'), { min: 0 });
			res.lsva_min = isValidNum(pricing.lsva_min, t('adminSiteInfo:minLsva'), { min: 0 });
			if (extra_text_from_volume) {
				res.extra_text_from_volume_volume_from = isValidNum(
					extra_text_from_volume.volume_from,
					t('adminSiteInfo:volumeDiscountFromM2'),
					{ min: 0 }
				);
			} else {
				res.extra_text_from_volume = {
					ok: false,
					err: t('adminSiteInfo:volumeDiscountInfoMissing'),
				};
			}

			// cd.glassdb
			res.max_glass_width = isValidNum(glassdb.max_glass_width, t('adminSiteInfo:maxGlassWidth'), {
				min: 0,
			});
			res.max_glass_height = isValidNum(
				glassdb.max_glass_height,
				t('adminSiteInfo:maxGlassHeight'),
				{
					min: 0,
				}
			);

			res.glass_translation_json = isValidJson(
				glassdb.glass_translation_json,
				t('adminSiteInfo:glassTranslationJson')
			);
			res.frg_csv = isValidCsv(glassdb.frg_csv, t('adminSiteInfo:fireResistantGlassCsv'), {
				min_rows: 10,
				min_cols: 19,
			});
			const db = uiHandlerRef.current?.glassDB;
			res.frg_db = isValidGlassDb(db, 'frg', { min: 10 });
			res.mid_csv = isValidCsv(glassdb.mid_csv, t('adminSiteInfo:middleGlassCsv'), {
				min_rows: 10,
				min_cols: 13,
			});
			res.mid_db = isValidGlassDb(db, 'mid', { min: 10 });
			res.out_csv = isValidCsv(glassdb.out_csv, t('adminSiteInfo:outsideGlassCsv'), {
				min_rows: 10,
				min_cols: 13,
			});
			res.out_db = isValidGlassDb(db, 'out', { min: 10 });
			res.uvalues = isValidJson(glassdb.uvalues, t('adminSiteInfo:uValuesJson'), {
				min: 2,
				max: 2,
			});
			res.uvalues_1_db = isValidUValueDb(db, '1-glazed', { min: 10 });
			res.uvalues_2_db = isValidUValueDb(db, '2-glazed', { min: 10 });
			res.uvalues_3_db = isValidUValueDb(db, '3-glazed', { min: 10 });
			res.veras_ids_json = isValidJson(glassdb.veras_ids_json, t('adminSiteInfo:verasIdsJson'));
			res.glass_prices = isValidGlassPrices(db, { minEntriesPerGlazing: 5 });

			// cd.datasheets
			if (datasheets && datasheets.datasheets_frg && datasheets.datasheets_frg.length > 0) {
				res.datasheets_frg = { ok: true };
			} else {
				res.datasheets_frg = { ok: false, err: t('adminSiteInfo:noFireResistantDatasheets') };
			}

			// cd.sound_dampening
			res.sound_dampening_table = isValidJson(
				sound_dampening.sound_dampening_table,
				t('adminSiteInfo:soundDampeningDb')
			);
			res.sound_dampening_generic_frgs = isValidJson(
				sound_dampening.generic_frgs,
				t('adminSiteInfo:genericFrgIds')
			);

			// cd.generic_frgs (price generic FRG mapping)
			res.price_generic_frgs = isValidJson(generic_frgs, 'Price Generic FRGs');
			if (res.price_generic_frgs.ok) {
				const parsed = typeof generic_frgs === 'string' ? JSON.parse(generic_frgs) : generic_frgs;
				const ei30Ids = parsed[GlassDB.GENERIC_EI30_ID] || [];
				const ei60Ids = parsed[GlassDB.GENERIC_EI60_ID] || [];
				const frgList = db?.db?.frg || [];
				const nameOf = id => {
					const glass = frgList.find(g => parseInt(g.id) === id);
					return glass ? `${glass.glasstype} (${id})` : `ID ${id}`;
				};
				const ei30Names = ei30Ids.map(nameOf).join(', ');
				const ei60Names = ei60Ids.map(nameOf).join(', ');
				if (ei30Ids.length === 0 || ei60Ids.length === 0) {
					res.price_generic_frgs = {
						ok: false,
						err: `EI30: [${ei30Names}], EI60: [${ei60Names}] — both must have at least 1 entry`,
					};
				} else if (ei30Ids.length < 3 || ei60Ids.length < 3) {
					res.price_generic_frgs = {
						ok: true,
						value: `EI30: ${ei30Names} (${ei30Ids.length} < 3), EI60: ${ei60Names} (${ei60Ids.length} < 3)`,
						warn: true,
					};
				} else {
					res.price_generic_frgs = {
						ok: true,
						value: `EI30: ${ei30Names}, EI60: ${ei60Names}`,
					};
				}
			}

			// cd.email
			res.email_signature_html = isNotEmptyString(
				email.email_signature_html,
				t('adminSiteInfo:emailSignature')
			);
			if (res.email_signature_html.ok) {
				res.email_signature_html.value = t('adminSiteInfo:emailSignaturePresent');
			}
			res.email_request = isNotEmptyString(email.email_request, t('adminSiteInfo:emailRequest'));
			res.email_order = isNotEmptyString(email.email_order, t('adminSiteInfo:emailOrder'));
			res.email_configurator = isNotEmptyString(
				email.email_configurator,
				t('adminSiteInfo:senderEmail')
			);
			res.email_new_user_to = isNotEmptyString(
				email.email_new_user_to,
				t('adminSiteInfo:newUserEmail')
			);
			res.email_new_user_cc = isNotEmptyString(
				email.email_new_user_cc,
				t('adminSiteInfo:newUserEmailCc')
			);

			Object.keys(res).forEach(k => {
				if (k !== 'allGood' && !res[k].ok) {
					res.allGood = false;
				}
			});

			if (mounted) setStatus(res);
		})();
		return () => {
			mounted = false;
		};
	}, [uiHandlerRef, t]);

	return (
		<div className="dashboard_site_info">
			<BlockHeading title={t('adminSiteInfo:heading')} iconName="config_code" />
			{status ? <FoldableStatusList status={status} /> : <Spinner />}
		</div>
	);
};

export default AdminDashboardSiteInfo;
